import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { KEYWORD_PINS_TYPE, type KeywordPinView } from '../pins/serverTypes';
import { queryKeys } from './queryKeys';

/**
 * Fetches the acting user's server-stored keyword pins from
 * `GET user/coffee/customizations/keywordPins` (see Real-Magic/Levitate-API#11465).
 *
 * Returns the raw `KeywordPinView[]` rows (custom pins + built-in overrides);
 * `useKeywordPins` overlays them onto `DEFAULT_KEYWORD_PINS` via `mergeKeywordPins`.
 * The list is per-user and not lead-scoped, so the query is always enabled.
 */
export function useKeywordPinsQuery(): UseQueryResult<KeywordPinView[], Error> {
	const userSession = useUserSession();

	return useQuery<KeywordPinView[], Error>({
		queryKey: queryKeys.focusedView.keywordPins(),
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<KeywordPinView[]>(
				ImpersonationBroker.composeApiUrl({ urlPath: `user/coffee/customizations/${KEYWORD_PINS_TYPE}` }),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value ?? [];
		},
		meta: { toastErrorMessage: 'Failed to load keyword pins' },
	});
}
